import { MeetingDays } from "../dtos/classes.type";

export function getAgeLabelByAgeValue(label: string): string {
  const regex = /P(\d+)Y\d+M-P(\d+)Y\d+M/;
  const match = label.match(regex);

  if (match) {
    const startYear = parseInt(match[1]);
    const endYear = parseInt(match[2]);

    if (startYear === endYear) {
      return `Age ${startYear} (Kids)`;
    } else if (startYear >= 7 && endYear >= 7) {
      return `Age ${startYear}-${endYear} (Children)`;
    } else {
      return `Age ${startYear}-${endYear} (Kids)`;
    }
  }
  return label;
}

export function getValueByAgeCode(timeString: string): string {
  const match = timeString.match(/P(\d+)Y(\d+)M/);

  if (!match) {
    return timeString; // Return original string if no match
  }

  const years = parseInt(match[1]);
  const months = parseInt(match[2]);

  // User-facing formatting: prefer "5" over "5Y0M" when months are zero;
  // fall back to "5y 6m" only when months are non-zero.
  if (years > 0 && months === 0) return String(years);
  if (years > 0) return `${years}y ${months}m`;
  if (months > 0) return `${months}m`;
  return "0";
}

const DAY_FULL_LABELS: Array<{ key: keyof MeetingDays; full: string; short: string; index: number }> = [
  { key: "mon", full: "Monday", short: "Mon", index: 0 },
  { key: "tue", full: "Tuesday", short: "Tue", index: 1 },
  { key: "wed", full: "Wednesday", short: "Wed", index: 2 },
  { key: "thu", full: "Thursday", short: "Thu", index: 3 },
  { key: "fri", full: "Friday", short: "Fri", index: 4 },
  { key: "sat", full: "Saturday", short: "Sat", index: 5 },
  { key: "sun", full: "Sunday", short: "Sun", index: 6 },
];

export function getDayByProperty(meeting_days: MeetingDays): string {
  if (!meeting_days) return "";
  const selected = DAY_FULL_LABELS.filter((d) => meeting_days[d.key]);
  if (!selected.length) return "";
  if (selected.length === 1) return selected[0].full;

  // Detect a contiguous range (e.g., Mon→Fri) and shorten to "Mon – Fri".
  const isContiguous = selected.every(
    (day, i) => i === 0 || day.index === selected[i - 1].index + 1
  );
  if (isContiguous) {
    return `${selected[0].short} – ${selected[selected.length - 1].short}`;
  }
  // Discontiguous days (e.g., Mon, Wed, Fri) render as a comma list.
  return selected.map((d) => d.short).join(", ");
}

export function convertAgeRange(range: string): string {
  const [start, end] = range.split("-");

  const startYears = parseInt(start.substring(1, 3));
  const startMonths = parseInt(start.substring(4, 6));
  const endYears = parseInt(end.substring(1, 3));
  const endMonths = parseInt(end.substring(4, 6));

  if (endYears === 99) {
    return `${startYears}+`;
  }

  if (startMonths === 0 && endMonths === 0) {
    return `${startYears}-${endYears}`;
  }

  if (startYears === 0) {
    const startFormatted = `${startMonths}m`;
    return `${startFormatted} to ${endYears}`;
  }

  return `${startYears}-${endYears}`;
}

export function parseAge(ageString: string) {
  const match = ageString.match(/P(\d+)Y(\d+)M-P(\d+)Y(\d+)M/);
  if (match) {
    const startMonths = parseInt(match[1]) * 12 + parseInt(match[2]);
    const endMonths = parseInt(match[3]) * 12 + parseInt(match[4]);
    return {
      start: startMonths,
      end: endMonths,
    };
  }
  return { start: 0, end: 0 }; // Return a default range if no match
}

export function getClassesPerWeek(meetingDays: MeetingDays): number {
  return Object.values(meetingDays).reduce(
    (count, value) => count + (value ? 1 : 0),
    0
  );
}
